import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { uploadFile, type UploadedFile } from "@client/lib/uploads"
import { useHasPermission } from "./session-context"
import { Select } from "./components/select"
import { DatePicker } from "./components/date-picker"
import { TimePicker } from "./components/time-picker"
import { Modal, ModalActions, ModalButton } from "./components/modal"
import { EventsPageSkeleton } from "./components/skeleton"
import { ContentPagination } from "./content-components"

type EventType = "workshop" | "showcase" | "graduation" | "field_trip" | "other"
type EventAudience = "participants_guardians" | "participants_only" | "public" | "staff_only"
type EventStatus = "draft" | "published" | "cancelled"
const PAGE_SIZE = 10

interface EventListItem {
  id: string
  code: string
  programId: string
  programName?: string
  title: string
  type: EventType
  date: string
  startTime: string
  endTime: string | null
  venue: string | null
  audience: EventAudience
  status: EventStatus
}

interface ProgramOption {
  id: string
  name: string
}

const EVENT_STATUS_COLORS: Record<EventStatus, { bg: string; text: string }> = {
  draft: { bg: "bg-gold-100", text: "text-gold-700" },
  published: { bg: "bg-green-100", text: "text-green-700" },
  cancelled: { bg: "bg-red-100", text: "text-red-700" },
}

const EVENT_TYPE_LABELS: Record<EventType, string> = {
  workshop: "Workshop",
  showcase: "Showcase",
  graduation: "Graduation",
  field_trip: "Field Trip",
  other: "Other",
}

const AUDIENCE_LABELS: Record<EventAudience, string> = {
  participants_guardians: "Participants + Guardians",
  participants_only: "Participants Only",
  public: "Public",
  staff_only: "Staff Only",
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  )
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  )
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  )
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  )
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  )
}

interface Speaker {
  name: string
  role: string
}

const EVENT_TYPES: { value: EventType; label: string }[] = [
  { value: "workshop", label: "Workshop" },
  { value: "showcase", label: "Showcase" },
  { value: "graduation", label: "Graduation" },
  { value: "field_trip", label: "Field trip" },
  { value: "other", label: "Other" },
]

const AUDIENCE_TYPES: { value: EventAudience; label: string }[] = [
  { value: "participants_guardians", label: "Participants + guardians" },
  { value: "participants_only", label: "Participants only" },
  { value: "public", label: "Public" },
  { value: "staff_only", label: "Staff only" },
]

function NewEventModal({
  isOpen,
  onClose,
  programs,
  defaultProgramId,
  onSuccess,
}: {
  isOpen: boolean
  onClose: () => void
  programs: ProgramOption[]
  defaultProgramId?: string
  onSuccess: () => void
}) {
  const [programId, setProgramId] = useState(defaultProgramId || "")
  const [title, setTitle] = useState("")
  const [eventType, setEventType] = useState<EventType>("workshop")
  const [date, setDate] = useState("")
  const [startTime, setStartTime] = useState("10:00")
  const [venue, setVenue] = useState("")
  const [audience, setAudience] = useState<EventAudience>("participants_guardians")
  const [speakers, setSpeakers] = useState<Speaker[]>([{ name: "", role: "" }])
  const [publishStatus, setPublishStatus] = useState<"draft" | "published">("draft")
  const [bannerFile, setBannerFile] = useState<UploadedFile | null>(null)
  const [bannerFileName, setBannerFileName] = useState("")
  const [bannerPreviewUrl, setBannerPreviewUrl] = useState<string | null>(null)
  const [bannerUploading, setBannerUploading] = useState(false)
  const [bannerUploadProgress, setBannerUploadProgress] = useState(0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (defaultProgramId) {
      setProgramId(defaultProgramId)
    }
  }, [defaultProgramId])

  useEffect(() => {
    return () => {
      if (bannerPreviewUrl) URL.revokeObjectURL(bannerPreviewUrl)
    }
  }, [bannerPreviewUrl])

  const handleAddSpeaker = () => {
    setSpeakers([...speakers, { name: "", role: "" }])
  }

  const handleRemoveSpeaker = (index: number) => {
    setSpeakers(speakers.filter((_, i) => i !== index))
  }

  const handleSpeakerChange = (index: number, field: "name" | "role", value: string) => {
    const updated = [...speakers]
    const speaker = updated[index]
    if (speaker) {
      speaker[field] = value
      setSpeakers(updated)
    }
  }

  const resetForm = () => {
    setProgramId(defaultProgramId || "")
    setTitle("")
    setEventType("workshop")
    setDate("")
    setStartTime("10:00")
    setVenue("")
    setAudience("participants_guardians")
    setSpeakers([{ name: "", role: "" }])
    setPublishStatus("draft")
    setBannerFile(null)
    setBannerFileName("")
    setBannerPreviewUrl(null)
    setBannerUploadProgress(0)
    setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      const validSpeakers = speakers.filter(s => s.name.trim() && s.role.trim())
      const res = await client.api.admin.programs.events.$post({
        json: {
          programId,
          title,
          type: eventType,
          date,
          startTime,
          venue: venue || undefined,
          audience,
          speakers: validSpeakers.length > 0 ? validSpeakers : undefined,
          bannerKey: bannerFile?.key,
        },
      })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }

      if (publishStatus === "published" && "data" in body) {
        const eventId = body.data.id
        await client.api.admin.programs.events[":id"].publish.$post({
          param: { id: eventId },
        })
      }

      onSuccess()
      onClose()
      resetForm()
    } catch {
      setError("Failed to create event")
    } finally {
      setSaving(false)
    }
  }

  const handleBannerChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ""
    if (!file || bannerUploading) return

    setBannerUploading(true)
    setBannerUploadProgress(0)
    setError(null)
    const previewUrl = URL.createObjectURL(file)
    setBannerFileName(file.name)
    setBannerPreviewUrl(previewUrl)
    try {
      const uploaded = await uploadFile(file, "event-banner", {
        onProgress: setBannerUploadProgress,
      })
      setBannerFile(uploaded)
    } catch (error) {
      setBannerFile(null)
      setBannerFileName("")
      setBannerPreviewUrl(null)
      setBannerUploadProgress(0)
      setError(error instanceof Error ? error.message : "Failed to upload banner")
    } finally {
      setBannerUploading(false)
    }
  }

  const selectedProgram = programs.find(p => p.id === programId)

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="New Event"
      subtitle="Add banner, speakers and details"
      maxWidth="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Banner Upload */}
        <div>
          <label className="block text-sm font-medium text-ink">Banner</label>
          <label htmlFor="event-banner" className="relative mt-2 flex h-32 cursor-pointer items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-gold-500/50 bg-cream-50 hover:bg-cream-100">
            <input
              id="event-banner"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleBannerChange}
            />
            {bannerPreviewUrl ? (
              <>
                <img src={bannerPreviewUrl} alt="Event banner preview" className="h-full w-full object-cover" />
                <div className="absolute inset-x-0 bottom-0 bg-ink/75 px-3 py-2 text-white">
                  <div className="flex items-center justify-between gap-3 text-xs">
                    <span className="truncate">{bannerFileName || bannerFile?.name}</span>
                    {bannerUploading && <span>{bannerUploadProgress}%</span>}
                  </div>
                  {bannerUploading && (
                    <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/30">
                      <div className="h-full rounded-full bg-white transition-[width]" style={{ width: `${bannerUploadProgress}%` }} />
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 text-sm text-ink/60">
                <UploadIcon className="h-5 w-5" />
                Click to upload event banner
              </div>
            )}
          </label>
        </div>

        {/* Program Selection */}
        <Select
          label="Program"
          value={selectedProgram?.name || "Select program"}
          onChange={(name) => {
            const prog = programs.find(p => p.name === name)
            if (prog) setProgramId(prog.id)
           }}
           options={programs.map(p => p.name)}
           searchable
           searchPlaceholder="Search programs..."
        />

        {/* Event Title & Type */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink">Event title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Term 2 tailoring showcase"
              className="mt-1 w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
          </div>
          <Select
            label="Event type"
            value={EVENT_TYPES.find(t => t.value === eventType)?.label || "Workshop"}
            onChange={(label) => {
              const type = EVENT_TYPES.find(t => t.label === label)
              if (type) setEventType(type.value)
            }}
            options={EVENT_TYPES.map(t => t.label)}
          />
        </div>

        {/* Date & Time */}
        <div className="grid grid-cols-2 gap-4">
          <DatePicker
            label="Date"
            value={date}
            onChange={setDate}
            min={new Date()}
          />
          <TimePicker
            label="Starts"
            value={startTime}
            onChange={setStartTime}
          />
        </div>

        {/* Venue */}
        <div>
          <label className="block text-sm font-medium text-ink">Venue</label>
          <input
            type="text"
            value={venue}
            onChange={(e) => setVenue(e.target.value)}
            placeholder="e.g., Learning Hub, Enugu"
            className="mt-1 w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
          />
        </div>

        {/* Audience */}
        <Select
          label="Audience"
          value={AUDIENCE_TYPES.find(t => t.value === audience)?.label || "Participants + guardians"}
          onChange={(label) => {
            const type = AUDIENCE_TYPES.find(t => t.label === label)
            if (type) setAudience(type.value)
          }}
          options={AUDIENCE_TYPES.map(t => t.label)}
        />

        {/* Speakers */}
        <div>
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-ink">Speakers / facilitators</label>
            <button
              type="button"
              onClick={handleAddSpeaker}
              className="inline-flex items-center gap-1 text-sm text-forest-700 hover:text-forest-900"
            >
              <PlusIcon className="h-4 w-4" />
              Add
            </button>
          </div>
          <div className="mt-2 space-y-2">
            {speakers.map((speaker, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={speaker.name}
                  onChange={(e) => handleSpeakerChange(index, "name", e.target.value)}
                  placeholder="Speaker name"
                  className="flex-1 rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                />
                <input
                  type="text"
                  value={speaker.role}
                  onChange={(e) => handleSpeakerChange(index, "role", e.target.value)}
                  placeholder="Role / topic"
                  className="flex-1 rounded-lg border border-gold-500 bg-cream-50 px-3 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                />
                {speakers.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveSpeaker(index)}
                    className="flex h-10 w-10 items-center justify-center rounded-lg text-ink/50 hover:bg-cream-100 hover:text-ink"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Publish Status */}
        <Select
          label="Publish"
          value={publishStatus === "draft" ? "Draft" : "Published"}
          onChange={(value) => setPublishStatus(value === "Draft" ? "draft" : "published")}
          options={["Draft", "Published"]}
        />

        {error && (
          <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</div>
        )}

        <ModalActions>
          <ModalButton onClick={onClose}>Cancel</ModalButton>
          <ModalButton type="submit" variant="primary" disabled={saving || bannerUploading || !title.trim() || !date || !programId}>
            {saving ? "Creating..." : "Create Event"}
          </ModalButton>
        </ModalActions>
      </form>
    </Modal>
  )
}

function formatEventDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

function formatTime(time: string): string {
  const [hours, minutes] = time.split(":")
  const h = Number.parseInt(hours || "0", 10)
  const period = h >= 12 ? "PM" : "AM"
  const hour12 = h % 12 || 12
  return `${hour12}:${minutes} ${period}`
}

export function EventsPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const canView = useHasPermission("events")
  const canMutate = useHasPermission("events_mutate")
  const [events, setEvents] = useState<EventListItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [programs, setPrograms] = useState<ProgramOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showNewModal, setShowNewModal] = useState(false)
  const [filterProgram, setFilterProgram] = useState<string>("")
  const [filterStatus, setFilterStatus] = useState<string>("")

  const defaultProgramId = searchParams.get("programId") || undefined

  const fetchPrograms = async () => {
    try {
      const res = await client.api.admin.programs.$get({ query: {} })
      const body = await res.json()
      if (res.ok && "data" in body) {
        setPrograms(body.data.items.map((p: { id: string; name: string }) => ({ id: p.id, name: p.name })))
      }
    } catch {
      // Silently fail
    }
  }

  const fetchEvents = async () => {
    try {
      const query: { programId?: string; status?: "draft" | "published" | "cancelled"; limit: string; offset: string } = {
        limit: String(PAGE_SIZE),
        offset: String((page - 1) * PAGE_SIZE),
      }
      if (filterProgram) query.programId = filterProgram
      if (filterStatus) query.status = filterStatus as "draft" | "published" | "cancelled"

      const res = await client.api.admin.programs.events.$get({ query })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        setEvents(body.data.items as EventListItem[])
        setTotal(body.data.total)
      }
    } catch {
      setError("Failed to load events")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (canView) {
      void fetchPrograms()
      void fetchEvents()
    } else {
      setLoading(false)
    }
  }, [canView])

  useEffect(() => {
    if (canView) {
      void fetchEvents()
    }
  }, [filterProgram, filterStatus, page])

  useEffect(() => {
    setPage(1)
  }, [filterProgram, filterStatus])

  if (!canView) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Events</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to view events.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return <EventsPageSkeleton />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-4xl">Events</h1>
          <p className="mt-1 text-sm text-ink/60">
            Workshops, showcases, graduations and more.
          </p>
        </div>
        {canMutate && (
          <button
            onClick={() => setShowNewModal(true)}
            className="inline-flex items-center gap-2 rounded-full bg-forest-950 px-5 py-3 text-sm font-medium text-white hover:bg-forest-900"
          >
            <PlusIcon className="h-5 w-5" />
            New Event
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="w-48">
          <Select
            label="Program"
            value={filterProgram ? programs.find(p => p.id === filterProgram)?.name || "All" : "All"}
            onChange={(name) => {
              if (name === "All") {
                setFilterProgram("")
              } else {
                const prog = programs.find(p => p.name === name)
                if (prog) setFilterProgram(prog.id)
              }
             }}
             options={["All", ...programs.map(p => p.name)]}
             searchable
             searchPlaceholder="Search programs..."
          />
        </div>
        <div className="w-40">
          <Select
            label="Status"
            value={filterStatus ? filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1) : "All"}
            onChange={(value) => {
              if (value === "All") {
                setFilterStatus("")
              } else {
                setFilterStatus(value.toLowerCase())
              }
            }}
            options={["All", "Draft", "Published", "Cancelled"]}
          />
        </div>
      </div>

      {/* Events List */}
      {error ? (
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-600">{error}</p>
        </div>
      ) : events.length === 0 ? (
        <div className="rounded-[20px] border-2 border-dashed border-gold-500 bg-white p-12 text-center">
          <CalendarIcon className="mx-auto h-12 w-12 text-ink/30" />
          <h3 className="mt-4 text-lg font-semibold text-ink">No events yet</h3>
          <p className="mt-2 text-sm text-ink/60">
            Get started by creating your first event.
          </p>
          {canMutate && (
            <button
              onClick={() => setShowNewModal(true)}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-forest-950 px-5 py-3 text-sm font-medium text-white hover:bg-forest-900"
            >
              <PlusIcon className="h-5 w-5" />
              Create event
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {events.map((event) => (
            <div
              key={event.id}
              onClick={() => navigate(`/admin/events/${event.id}`)}
              className="cursor-pointer rounded-[20px] border border-gold-500/30 bg-white p-5 transition-colors hover:bg-cream-50"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wider text-ink/50">
                  {event.programName || "Program"}
                </span>
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${
                    EVENT_STATUS_COLORS[event.status].bg
                  } ${EVENT_STATUS_COLORS[event.status].text}`}
                >
                  {event.status}
                </span>
              </div>

              <h3 className="mt-3 text-lg font-semibold text-ink">{event.title}</h3>

              <div className="mt-3 space-y-1.5 text-sm text-ink/70">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  {formatEventDate(event.date)}
                </div>
                <div className="flex items-center gap-2">
                  <ClockIcon className="h-4 w-4" />
                  {formatTime(event.startTime)}
                  {event.endTime && ` - ${formatTime(event.endTime)}`}
                </div>
                {event.venue && (
                  <div className="flex items-center gap-2">
                    <MapPinIcon className="h-4 w-4" />
                    {event.venue}
                  </div>
                )}
              </div>

              <div className="mt-4 flex items-center gap-2">
                <span className="rounded-full bg-cream-100 px-2.5 py-1 text-xs font-medium text-ink/70">
                  {EVENT_TYPE_LABELS[event.type]}
                </span>
                <span className="rounded-full bg-cream-100 px-2.5 py-1 text-xs font-medium text-ink/70">
                  {AUDIENCE_LABELS[event.audience]}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <ContentPagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} disabled={loading} />

      <NewEventModal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        programs={programs}
        defaultProgramId={defaultProgramId}
        onSuccess={() => void fetchEvents()}
      />
    </div>
  )
}
